import {
  FC, ReactNode, RefObject, useMemo,
} from 'react';
import { animated, useSpring } from 'react-spring';
import SvgIcon from '@picsart/rc/old/SvgIcon';
import { useTheme } from 'react-jss';

import { ITheme } from 'types';
import useToggle from 'hooks/common/useToggle';

import {
  eraserTools,
  eraserToolsNew,
  EraserToolsEnum,
  ERASER_SETTINGS_SIDEBAR_WIDTH,
  ERASER_SIDEBAR_WIDTH,
  ERASER_SIDEBAR_WIDTH_DARK,
  ERASER_SETTINGS_SIDEBAR_WIDTH_DARK,
  ERASER_PAGE_PADDING,
  ERASER_GAP,
} from '../config';
import { useSidebarStyles } from '../styles';

import EraserTool from './EraserTool';
import HistoryTools from './HistoryTools';

interface IEraserSidebar {
  onReset: () => void;
  onInvert: () => void;
  brushDisabled?: boolean;
  hasPersonCutout: boolean;
  children: ReactNode;
  activeTool: EraserToolsEnum | null;
  onToolSelect: (type: EraserToolsEnum) => void;
  sidebarContainerRef: RefObject<HTMLDivElement>;
  isLoading?: boolean;
  isDisabled?: boolean;
  isDarkMode: boolean;
}

const EraserSidebar: FC<IEraserSidebar> = ({
  onReset,
  onInvert,
  children,
  activeTool,
  onToolSelect,
  brushDisabled = false,
  hasPersonCutout,
  sidebarContainerRef,
  isLoading,
  isDisabled,
  isDarkMode,
}) => {
  const theme = useTheme<ITheme>();

  const [open, toggleOpen] = useToggle(true);

  const classes = useSidebarStyles({
    isDarkMode,
    open,
  });

  const sidebarAnimation = useSpring({
    config: { duration: 80 },
    marginLeft: open ? 0 : !isDarkMode ? -ERASER_SETTINGS_SIDEBAR_WIDTH - 10 : -ERASER_SETTINGS_SIDEBAR_WIDTH_DARK,
    opacity: open ? 1 : 0,
  });

  const sidebarIconAnimation = useSpring({
    config: { duration: 80 },
    left: open
      ? !isDarkMode
        ? ERASER_SETTINGS_SIDEBAR_WIDTH + ERASER_SIDEBAR_WIDTH
        : ERASER_SETTINGS_SIDEBAR_WIDTH_DARK + ERASER_SIDEBAR_WIDTH_DARK + ERASER_PAGE_PADDING + ERASER_GAP
      : !isDarkMode
        ? ERASER_SIDEBAR_WIDTH
        : ERASER_SIDEBAR_WIDTH_DARK + ERASER_PAGE_PADDING,
  });

  const filteredTools = useMemo(
    () => (isDarkMode ? eraserToolsNew : eraserTools).filter(({ type }) => type !== EraserToolsEnum.CUT_OUT || hasPersonCutout),
    [hasPersonCutout, isDarkMode],
  );

  return (
    <div className={classes.eraserSidebarContainer} ref={sidebarContainerRef}>
      <div className={classes.eraserDrawnToolsContainer}>
        <div className={classes.eraserDrawnToolsContainerScroller}>
          <div className={classes.sliderBarToolsContainer}>
            {filteredTools.map(tool => (
              <EraserTool
                tool={tool}
                key={tool.name}
                onToolSelect={onToolSelect}
                active={tool.type === activeTool}
                disabled={tool.type === EraserToolsEnum.BRUSH && brushDisabled}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
          <HistoryTools
            onReset={onReset}
            onInvert={onInvert}
            onToolSelect={onToolSelect}
            toolsNameClass={classes.toolIconName}
            isLoading={isLoading}
            isDisabled={isDisabled}
            isDarkMode={isDarkMode}
          />
        </div>

      </div>
      <animated.div style={sidebarAnimation} className={classes.eraserDrawnSettingsContainer}>
        {children}
      </animated.div>
      {(activeTool !== EraserToolsEnum.CUT_OUT) && (!isDarkMode ? (
        <animated.div
          className={classes.visibilitySidebarIcon}
          onClick={toggleOpen}
          style={sidebarIconAnimation}
        >
          <SvgIcon
            name={open ? 'arrow-small-left-gen' : 'arrow-small-right-gen'}
            className={classes.arrowIcon}
          />
        </animated.div>
      ) : (
        <animated.div
          className={classes.visibilitySidebarButton}
          onClick={toggleOpen}
          style={sidebarIconAnimation}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="72" viewBox="0 0 20 72" fill="none">
            <path
              d="M0 0C0 4.41828 3.58172 8 8 8H12C16.4183 8 20 11.5817 20 16V56C20 60.4183 16.4183 64 12 64H8C3.58172 64 0 67.5817 0 72V0Z"
              fill={`var(--layout-bg-color, ${theme.colors.backgrounds.tint1})`}
            />
          </svg>
          <SvgIcon name="arrow-left-gen" className={classes.arrowIconNS} />
        </animated.div>
      )
      )}
    </div>
  );
};

export default EraserSidebar;
